import { useState, useCallback } from "react";
import { useQueryState } from "nuqs";
import { Link, useParams } from "wouter";
import { Download, PlugsConnected, Package } from "@phosphor-icons/react";
import type { ActionResult } from "scantling-common";
import { phaseRegistry } from "../registry.js";
import { PhaseTile, useInvalidateLists } from "../components.js";
import { IntegrationTable, ItemTable } from "../table.js";
import { ActionButton } from "../action_button.js";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from "@/components/ui/empty-state";

const InboxPage = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [tab, setTab] = useQueryState("tab", { defaultValue: "items" });
  const [status, setStatus] = useState<string | null>(null);
  const invalidate = useInvalidateLists();

  const handleCreateSuccess = useCallback(
    (result: ActionResult) => {
      const slug = result.data?.slug as string;
      setStatus(`Item created: ${slug}`);
      invalidate("listItemVersions");
    },
    [invalidate],
  );

  const handleCreateError = useCallback((err: Error) => {
    setStatus(`Error: ${err.message}`);
  }, []);

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <div className="flex items-center justify-between">
        <TabsList>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="sources">Sources</TabsTrigger>
        </TabsList>
        <div className="flex items-center gap-2">
          {status && (
            <span className="text-sm text-muted-foreground">{status}</span>
          )}
          {tab === "sources" ? (
            <Button asChild>
              <Link href={`/o/${orgSlug}/sources`}>Add source</Link>
            </Button>
          ) : (
            <ActionButton
              verb="createItem"
              onActionSuccess={handleCreateSuccess}
              onActionError={handleCreateError}
            >
              Create Item
            </ActionButton>
          )}
        </div>
      </div>
      <TabsContent value="items">
        <ItemTable
          search="phase:inbox"
          emptyState={
            <EmptyState>
              <EmptyStateIcon>
                <Package />
              </EmptyStateIcon>
              <EmptyStateTitle>No items in inbox</EmptyStateTitle>
              <EmptyStateDescription>
                Create your first item to get started.
              </EmptyStateDescription>
              <EmptyStateActions>
                <ActionButton
                  verb="createItem"
                  onActionSuccess={handleCreateSuccess}
                  onActionError={handleCreateError}
                >
                  Create Item
                </ActionButton>
              </EmptyStateActions>
            </EmptyState>
          }
        />
      </TabsContent>
      <TabsContent value="sources">
        <IntegrationTable
          search="kind:source"
          emptyState={
            <EmptyState>
              <EmptyStateIcon>
                <PlugsConnected />
              </EmptyStateIcon>
              <EmptyStateTitle>No sources configured</EmptyStateTitle>
              <EmptyStateDescription>
                Connect external sources to feed items in automatically.
              </EmptyStateDescription>
              <EmptyStateActions>
                <Button asChild>
                  <Link href={`/o/${orgSlug}/sources`}>Add source</Link>
                </Button>
              </EmptyStateActions>
            </EmptyState>
          }
        />
      </TabsContent>
    </Tabs>
  );
};
const InboxTile = () => {
  return (
    <PhaseTile phase="inbox">
      <Download size={24} />
    </PhaseTile>
  );
};

phaseRegistry.register({
  type: "inbox",
  order: 1,
  page: InboxPage,
  tile: InboxTile,
});
